import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Label, textVariants } from "@/src/components/Text/Text";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/src/components/shad/card";
import api from "@/src/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useCountries } from "@/src/hooks/useCountries";
import { useTranslation } from "react-i18next";

const addressState = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
  country: z.number().optional(),
});
const Address = ({ profile }: { profile: PaintProfile | undefined }) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.settings.profile.address.${key}`);
  type addressVals = z.infer<typeof addressState>;
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  // const { user } = useAppSelector((state) => state.user);

  const { countries } = useCountries();

  const { register, handleSubmit, setValue, watch } = useForm<addressVals>({
    resolver: zodResolver(addressState),
  });

  const selectedCountry = countries?.info.find(
    (item) => item?.id === watch("country")
  );

  const addrressChange = async (data: addressVals) => {
    const res = await api.post(apiRoutes.addressChange, data);
    return res?.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["address_change"],
    mutationFn: addrressChange,
    onSuccess: (data) => {
      if (data?.success) {
        queryClient.invalidateQueries(["fetch_profile"]);
        // refetch();
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
    onError: (err: AxiosError) => {
      toast.error(err.message);
    },
  });

  const submit: SubmitHandler<addressVals> = (data) => {
    mutate(data);
  };

  useEffect(() => {
    if (profile) {
      setValue("city", profile?.city);
      setValue("state", profile?.state);
      setValue("street", profile?.location);
      setValue("zip_code", profile?.zip_code);
      setValue("country", profile?.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card>
        <CardHeader>
          <CardTitle>{trans("title")}</CardTitle>
          <CardDescription>{trans("desc")}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid  md:grid-cols-2 gap-3">
            <InputComponent
              label={trans("form.street")}
              type={"text"}
              {...register("street", { required: true })}
              defaultValue={profile?.location}
            />

            <InputComponent
              label={trans("form.city")}
              type={"text"}
              {...register("city", { required: true })}
              defaultValue={profile?.city}
            />
            <InputComponent
              label={trans("form.state")}
              type={"text"}
              {...register("state", { required: true })}
              defaultValue={profile?.state}
            />
            <InputComponent
              label={trans("form.zipcode")}
              type={"text"}
              {...register("zip_code", { required: true })}
              defaultValue={profile?.zip_code}
            />
            <div>
              <Label>{trans("form.country")}</Label>
              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <div className=" grid ">
                  <PopoverTrigger asChild>
                    <Button
                      role="combobox"
                      className={cn(
                        "focus:outline-indigo-400 dark:focus:outline-grey-400 focus:outline-none dark:bg-grey-600 bg-input-field text-black dark:text-white dark:placeholder:text-gray-200 w-full border py-2"
                      )}
                    >
                      {watch("country") ? (
                        <span className="flex  w-full gap-2">
                          <img
                            src={selectedCountry?.image}
                            alt=""
                            className="h-5 w-5"
                          />
                          <span>{selectedCountry?.name}</span>
                        </span>
                      ) : (
                        <span className="text-left w-full">Select country</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className={cn(
                      ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                    )}
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder={`Search country`} />
                      <CommandEmpty>Nothing found.</CommandEmpty>
                      <CommandGroup>
                        {countries?.info.map((item, index) => (
                          <CommandItem
                            key={index}
                            onSelect={() => {
                              setIsOpen(false);
                              // setCountryCode(item?.short_code_iso3)
                              setValue("country", item?.id);
                            }}
                            value={item?.name}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                watch("country") === item?.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />

                            <div className="flex items-center h-1">
                              <span className="text-lg mr-2">
                                <img
                                  src={item?.image}
                                  alt=""
                                  className="h-5 w-5"
                                />
                              </span>
                              <span>{item?.name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </div>
              </Popover>
            </div>
          </div>
        </CardContent>
        <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
          <span className={textVariants({ variant: "desc", font: "medium" })}>
            {trans("footerText")}
          </span>

          <Button
            variant="primary"
            onClick={handleSubmit(submit)}
            loading={isLoading}
          >
            {trans("form.saveButton")}
          </Button>
        </CardHeader>
      </Card>
    </form>
  );
};

export default Address;
